import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { usePrivatePage } from "../lib/seo";
import { Header } from "../components/Header";
import { OrderLine } from "../components/order/OrderLine";
import { CompareSizes } from "../components/CompareSizes";
import { individualDelivery } from "../lib/delivery";
import { DiscountBox, FulfilmentChoice, VoucherBox } from "../components/order/CheckoutExtras";
import { readCart, writeCart, addToCart } from "../lib/cart";
import { CustomerForm, emptyCustomer } from "../components/order/CustomerForm";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { api, errMsg } from "../lib/api";
import { preloadDesigns } from "../lib/images";
import { svgToPng } from "../lib/exportPng";
import { track, trackAddToCart, trackBeginCheckout } from "../lib/analytics";
import { useKeyboardOpen } from "../lib/useKeyboard";
import { useIsMobile } from "../components/designer/MobileTabs";
import { sizeOf, eur, VIEW } from "../lib/shield";

const PRINT_DPI = 150;
const bleedFor = (size) => 3 / (sizeOf(size).h / VIEW.h); // 3 mm bleed in artwork units
const scaleFor = (size) => ((sizeOf(size).h / 25.4) * PRINT_DPI) / (VIEW.h + 2 * bleedFor(size));

// Full-size print artwork (JPEG with bleed) for one order line, stored for the print PDF.
const makePrintFile = async (svg, size) => {
  const blob = await svgToPng(svg, { scale: scaleFor(size), type: "image/jpeg", quality: 0.9, watermark: false });
  const form = new FormData();
  form.append("file", new File([blob], "print.jpg", { type: "image/jpeg" }));
  form.append("purpose", "print");
  return (await api.post("/uploads", form)).data.id;
};

export default function Order() {
  usePrivatePage("Checkout");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const teamId = params.get("team");
  useKeyboardOpen(useIsMobile());
  const [lines, setLines] = useState(null);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [coupon, setCoupon] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [ack, setAck] = useState(false);
  const [choice, setChoice] = useState(null);
  const [paying, setPaying] = useState("");
  const printRefs = useRef({});
  const justAdded = useRef([]);
  const gaItems = (ls) => ls.map((l) => ({ item_id: `shield-${l.size}`, item_name: "Personalised shield", item_variant: sizeOf(l.size).name, price: sizeOf(l.size).price / 100, quantity: l.quantity || 1 }));

  useEffect(() => {
    if (!teamId && params.get("d")) {
      const inCart = new Set(readCart().map((c) => c.draftId));
      const ids = params.get("d").split(",").filter(Boolean);
      justAdded.current = [...new Set([...justAdded.current, ...ids.filter((id) => !inCart.has(id))])];
      ids.forEach(addToCart);
      nav("/order", { replace: true });
      return;
    }
    const cart = teamId ? [] : readCart();
    const saved = Object.fromEntries(cart.map((c) => [c.draftId, c]));
    const req = teamId ? api.get(`/teams/${teamId}`).then((r) => r.data.players)
      : cart.length ? api.get(`/drafts?ids=${cart.map((c) => c.draftId).join(",")}`).then((r) => r.data) : Promise.resolve([]);
    req.then((drafts) => setLines(drafts.map((d) => ({ draft: d, size: saved[d.id]?.size || d.design.size || "medium", quantity: saved[d.id]?.quantity ?? 1 }))))
      .catch((e) => { toast.error(errMsg(e)); setLines([]); });
  }, [teamId, params, nav]);
  useEffect(() => {
    if (!lines || !justAdded.current.length) return;
    trackAddToCart(gaItems(lines.filter((l) => justAdded.current.includes(l.draft.id))));
    justAdded.current = [];
  }, [lines]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = (lines || []).reduce((t, l) => t + sizeOf(l.size).price * l.quantity, 0);
  const fullDelivery = !subtotal ? 0 : individualDelivery(lines.filter((l) => l.quantity > 0).map((l) => l.size));
  const clubOrder = !!lines?.length && lines.every((l) => l.draft.team_id);
  const fulfilment = choice === (clubOrder ? "collect" : "club") ? "deliver" : choice || "deliver";
  const delivery = fulfilment === "deliver" ? fullDelivery : 0;
  const discount = coupon ? (lines || []).reduce((t, l) => t + Math.round(sizeOf(l.size).price * coupon.percent / 100) * l.quantity, 0) : 0;
  const gross = subtotal - discount + delivery;
  const covered = voucher ? Math.min(voucher.balance, gross) : 0;
  const voucherOff = gross - covered > 0 && gross - covered < 50 ? Math.max(gross - 50, 0) : covered; // Stripe's €0.50 minimum
  const total = gross - voucherOff;
  useEffect(() => { if (lines && !teamId) writeCart(lines.map((l) => ({ draftId: l.draft.id, size: l.size, quantity: l.quantity }))); }, [lines, teamId]);
  const update = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => setLines((ls) => ls.filter((_, j) => j !== i));

  const pay = async (e) => {
    e.preventDefault();
    const chosen = lines.filter((l) => l.quantity > 0);
    trackBeginCheckout(gaItems(chosen));
    try {
      setPaying("Preparing print files…");
      await preloadDesigns(chosen.map((l) => l.draft.design));
      const items = [];
      for (const [i, l] of chosen.entries()) {
        setPaying(`Preparing print file ${i + 1} of ${chosen.length}…`);
        const print_file_id = await makePrintFile(printRefs.current[l.draft.id], l.size);
        items.push({ draft_id: l.draft.id, size: l.size, quantity: l.quantity, print_file_id, bleed: bleedFor(l.size) });
      }
      setPaying("Opening secure checkout…");
      track("begin_checkout", { value: total / 100, currency: "EUR", items: items.map((it) => ({ item_id: `shield_${it.size}`, quantity: it.quantity })) });
      const { data } = await api.post("/orders", { items, customer, team_id: teamId, origin_url: window.location.origin, coupon: coupon?.code, voucher: voucher?.code, fulfilment, personalised_ack: ack });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setPaying(""); }
  };

  return (
    <div className="min-h-screen pb-10">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-10">
        <div className="eyebrow">Checkout</div>
        <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">Order your shields</h1>
        {lines === null ? <p className="py-10 text-slate-500">Loading…</p> : lines.length === 0 ? (
          <div className="py-10" data-testid="order-empty"><p className="text-slate-500">Your basket is empty – design a shield and tap Order to add it.</p>
            <button type="button" onClick={() => nav("/design")} className="btn-blaze mt-4 px-5 py-2.5 text-sm" data-testid="order-empty-design-button">Design a shield</button></div>
        ) : (
          <form onSubmit={pay} className="mt-8 grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
            <section className="min-w-0 space-y-3" data-testid="order-lines">
              <div className="flex justify-end"><CompareSizes testId="order-compare-sizes-button" /></div>
              {lines.map((l, i) => <OrderLine key={l.draft.id} line={l} onChange={(p) => update(i, p)} onRemove={teamId ? undefined : () => remove(i)} />)}
              {!teamId && (
                <div className="flex flex-wrap items-center gap-3 border-2 border-dashed border-slate-300 p-4 text-sm" data-testid="order-add-more">
                  <span className="font-semibold">Ordering for more than one child?</span>
                  <button type="button" onClick={() => nav("/design")} className="border-2 border-slate-900 px-3 py-1.5 text-xs font-bold uppercase hover:bg-slate-100" data-testid="order-add-another-button">+ Add another shield</button>
                  <span className="text-xs text-slate-500">Your basket is saved – using a squad link? Go back to it and pick your next child.</span>
                </div>
              )}
            </section>
            <aside className="space-y-5 self-start border-2 border-slate-900 bg-white p-5 shadow-[6px_6px_0_0_#0f172a]">
              <FulfilmentChoice value={fulfilment} onChange={setChoice} club={clubOrder} deliveryLabel={`${eur(fullDelivery)} – delivered to your home anywhere in Ireland`} />
              <CustomerForm value={customer} onChange={setCustomer} contactOnly={fulfilment !== "deliver"} note={fulfilment === "collect" ? "No address needed – collect from Business Print, Unit 3B Ballymount Drive, Walkinstown, D12 W028. We'll email you when it's ready." : undefined} />
              <DiscountBox coupon={coupon} onChange={setCoupon} />
              <VoucherBox voucher={voucher} onChange={setVoucher} email={customer.email} />
              <div className="space-y-1 border-t-2 border-slate-900 pt-4 text-sm">
                <div className="flex justify-between"><span>Shields</span><span data-testid="order-subtotal">{eur(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between font-bold text-lime-700"><span>Discount ({coupon.percent}%)</span><span data-testid="order-discount">−{eur(discount)}</span></div>}
                <div className="flex justify-between"><span>{fulfilment === "collect" ? "Click & collect" : "Delivery (Ireland)"}</span><span data-testid="order-delivery">{fulfilment === "club" ? "Free – with your club's order" : fulfilment === "collect" ? "Free" : eur(delivery)}</span></div>
                {voucherOff > 0 && <div className="flex justify-between font-bold text-red-600"><span>Gift voucher</span><span data-testid="order-voucher">−{eur(voucherOff)}</span></div>}
              </div>
              <div className="flex items-baseline justify-between">
                <span className="font-display text-lg font-extrabold uppercase">Total</span>
                <span className="font-cond text-3xl font-bold" data-testid="order-total">{eur(total)}</span>
              </div>
              <p className="-mt-3 text-xs text-slate-500" data-testid="order-delivery-note">{fulfilment === "club" ? "Prices include VAT. Your shield is printed with your squad and delivered to your coach or club." : fulfilment === "collect" ? "Prices include VAT. Free collection from Business Print, Walkinstown – we'll email you when it's ready." : "Prices include VAT. Delivery: €8 Small/Medium, €18 if the order includes Large or XL."}</p>
              <label className={`flex cursor-pointer items-start gap-3 border-2 p-3 text-xs leading-relaxed ${ack ? "border-slate-900 bg-lime-50" : "border-amber-400 bg-amber-50"}`} data-testid="order-personalised-ack">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-slate-900" data-testid="order-personalised-ack-checkbox" />
                <span><strong>Personalised product:</strong> each shield is custom-made to your design, with your photo, names and details, and printed to order. Because it's unique to you, it has no resale value, and we can't accept returns or refunds for a change of mind once your order is placed. Please check spelling, photos and size carefully. This doesn't affect your rights if an item arrives faulty or damaged. <strong>I understand and agree.</strong> See our <a href="/terms" target="_blank" rel="noreferrer" className="font-bold underline" data-testid="order-terms-link">terms of sale</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="font-bold underline" data-testid="order-privacy-link">privacy policy</a>.</span>
              </label>
              <button type="submit" disabled={paying || gross === 0 || !ack} className="btn-blaze flex w-full items-center justify-center gap-2 px-4 py-3 text-base" data-testid="order-pay-button">
                {paying ? <><Loader2 className="h-4 w-4 animate-spin" /> {paying}</> : <><Lock className="h-4 w-4" /> {total === 0 ? "Place order – paid by gift voucher" : "Pay securely with Stripe"}</>}
              </button>
            </aside>
          </form>
        )}
      </main>
      <div aria-hidden className="pointer-events-none fixed -left-[5000px] top-0 w-[400px]">
        {(lines || []).map((l) => (
          <ShieldPreview key={l.draft.id} ref={(el) => { printRefs.current[l.draft.id] = el; }} design={l.draft.design} bleed={bleedFor(l.size)} testId={`print-shield-${l.draft.id}`} />
        ))}
      </div>
    </div>
  );
}
